import { computed, getCurrentInstance } from 'vue'
import { useMainStore } from '@/store/main'
import { convertHeight, convertKgToStAndLbs, convertWeightsRound } from '@/helpers/userData'

/**
 * Composable replacement for HeightAndWeightHelperMixin.
 *
 * Provides weight/height formatting methods with unit systems.
 *
 * Usage in setup():
 *   const { weightWithUnitsSingleUnit, weightWithUnitsUniversal, heightWithUnits, getHeightUnitSystem, getWeightUnitSystem } = useHeightAndWeight()
 */
export function useHeightAndWeight() {
	const mainStore = useMainStore()
	const instance = getCurrentInstance()

	const getHeightUnitSystem = computed(() => mainStore.getHeightUnitSystem)
	const getWeightUnitSystem = computed(() => mainStore.getWeightUnitSystem)

	function weightWithUnitsSingleUnit(weight, unitSystem) {
		const unit = unitSystem || getWeightUnitSystem.value
		return instance.proxy.$tc(`common.${unit}_plural`, weight)
	}

	function weightWithUnitsUniversal(weight) {
		if (getWeightUnitSystem.value === 'st') {
			const { st, lbs } = convertKgToStAndLbs(weight)
			if (lbs > 0) {
				return `${weightWithUnitsSingleUnit(st)} ${weightWithUnitsSingleUnit(lbs, 'lbs')}`
			}
			return weightWithUnitsSingleUnit(st)
		}
		return weightWithUnitsSingleUnit(convertWeightsRound(weight, getWeightUnitSystem.value))
	}

	function heightWithUnits(cm) {
		const proxy = instance.proxy
		if (getHeightUnitSystem.value === 'cm') return `${cm} ${proxy.$t('common.cm')}`
		const heightInFt = convertHeight(cm, 'ft')
		if (!heightInFt) return ''
		const [ft, inch] = heightInFt.split(' ')
		if (inch === '0') return `${ft} ${proxy.$t('common.ft')}`
		return `${ft} ${proxy.$t('common.ft')} ${Math.ceil(inch)} ${proxy.$t('common.in')}`
	}

	return {
		getHeightUnitSystem,
		getWeightUnitSystem,
		weightWithUnitsSingleUnit,
		weightWithUnitsUniversal,
		heightWithUnits,
	}
}
